import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Wifi, Smartphone, Cable, Router, CreditCard, Zap, Check } from 'lucide-react';
import { affiliatePrograms } from '../data/affiliatePrograms';
import { useLanguage } from '../i18n/LanguageContext';

const iconMap: Record<string, React.ReactNode> = {
  wifi: <Wifi className="w-6 h-6" />,
  smartphone: <Smartphone className="w-6 h-6" />,
  cable: <Cable className="w-6 h-6" />,
  router: <Router className="w-6 h-6" />,
  creditcard: <CreditCard className="w-6 h-6" />,
  zap: <Zap className="w-6 h-6" />,
};

const AffiliatePrograms: React.FC = () => {
  const { t, lang } = useLanguage();

  return (
    <section className="section-padding bg-neutral-50">
      <div className="container-custom">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="badge-primary mb-3">{t('home.affiliateBadge')}</span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-neutral-900 mb-3">
            {t('home.affiliateTitle')}
          </h2>
          <p className="text-neutral-500 max-w-2xl mx-auto">
            {t('home.affiliateDesc')}
          </p>
        </motion.div>

        <div className="space-y-10 max-w-6xl mx-auto">
          {affiliatePrograms.map((program, i) => {
            const offerCount = program.offers.length;
            // O2, 1&1 and Blau have 2 offers → fill width; Vodafone has 3 → standard grid
            const gridClass =
              offerCount === 2
                ? 'grid grid-cols-1 sm:grid-cols-2 gap-5'
                : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5';

            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                {/* Program header bar */}
                <div
                  className="flex items-center gap-3 mb-5"
                >
                  <div
                    className="min-w-[44px] h-11 px-3 rounded-xl flex items-center justify-center shadow-md"
                    style={{ backgroundColor: program.brandColor }}
                  >
                    <span className="text-white font-bold text-base whitespace-nowrap">
                      {program.id === 'o2' ? 'O₂' : program.name}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-neutral-900">{program.name}</h3>
                    <span className="text-xs text-neutral-400">
                      {lang === 'EN' ? 'Official partner' : 'Offizieller Partner'}
                    </span>
                  </div>
                </div>

                {/* Offer cards */}
                <div className={`${gridClass} items-stretch`}>
                  {program.offers.map((offer, j) => (
                    <motion.div
                      key={offer.id}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: j * 0.08 }}
                      whileHover={{ y: -6 }}
                      className="group flex flex-col h-full rounded-2xl overflow-hidden shadow-soft hover:shadow-premium transition-all duration-300"
                      style={{ border: `1px solid ${program.brandBorder}` }}
                    >
                      <>
                        {/* Visual header */}
                        <a
                          href={offer.url}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          className="block relative w-full"
                          style={program.id === 'vodafone'
                            ? { backgroundColor: '#1a0000' }
                            : { backgroundColor: program.brandBgLight }}
                        >
                          {offer.image ? (
                            <div
                              className="w-full overflow-hidden"
                              style={{
                                ...(program.id === '1und1' ? { height: '220px' } : {}),
                                backgroundColor: program.id === 'vodafone' ? '#1a0000' : program.brandBgLight,
                              }}
                            >
                              <img
                                src={offer.image}
                                alt={program.name}
                                className={`block w-full ${program.id === '1und1' ? 'h-full' : ''}`}
                                style={{ objectFit: 'cover', objectPosition: 'center' }}
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className={`relative overflow-hidden bg-gradient-to-br ${program.gradient} w-full`} style={{ minHeight: '220px' }}>
                              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
                              <div className="absolute -left-10 -bottom-10 w-28 h-28 rounded-full bg-white/5" />
                            </div>
                          )}
                        </a>

                        {/* Body */}
                        <div
                          className="p-5 flex flex-col flex-1 justify-between"
                          style={{ backgroundColor: program.brandBgLight }}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span
                              className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow-sm shrink-0"
                              style={{ backgroundColor: program.brandColor }}
                            >
                              {iconMap[offer.icon]}
                            </span>
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                              style={{ backgroundColor: program.brandColor, color: 'white' }}
                            >
                              {lang === 'EN' ? offer.tagEn : offer.tag}
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-neutral-800 leading-relaxed mb-4">
                            {lang === 'EN' ? offer.labelEn : offer.label}
                          </p>
                          <div className="flex flex-wrap items-center gap-3">
                            <a
                              href={offer.url}
                              target="_blank"
                              rel="noopener noreferrer sponsored"
                              className="inline-flex items-center gap-1.5 text-sm font-bold transition-all hover:gap-2.5"
                              style={{ color: program.brandColor }}
                            >
                              {t('home.affiliateCta')}
                              <ArrowRight className="w-4 h-4" />
                            </a>
                            {offer.url2 && (
                              <a
                                href={offer.url2}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                className="inline-flex items-center gap-1.5 text-sm font-bold transition-all hover:gap-2.5"
                                style={{ color: program.brandColor }}
                              >
                                {lang === 'EN' ? offer.label2En : offer.label2}
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AffiliatePrograms;
